import { CircleLazyImage } from '@bilibili-maoer/prelude'
import { api } from '@bilibili-maoer/web-utils'

import { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import { Stack, styled } from '@mui/material'
import moment from 'moment'
import useMount from 'react-use/esm/useMount'
import { match } from 'ts-pattern'

import { Button } from '~/core/components/button'
import { imageOptimizeRendering } from '~/core/components/image'
import { StyledRouterLink } from '~/core/components/link'
import { Title } from '~/core/components/title'
import { asMissEvanPagination } from '~/core/pagination'
import { TTime, initDateTimeRange } from '~/core/utils/date'
import { DateRange } from '~/features/guild/components/date-range'
import { GuildGuardWithRole } from '~/features/guild/components/router-guard'
import { DataBlocksTitle, GuildTitle } from '~/features/guild/components/title'
import { DateRangeWrapper, Form } from '~/features/guild/components/wrappers'
import { GUILD_MIDDLE_TITLE } from '~/features/guild/constants'
import { GuildReportInfoDataTable } from '~/features/guild/report/component/report-detail-data-table'
import { GuildReportInfo } from '~/features/guild/report/component/report-info'
import { LIMIT_RANGE_OF_DAYS } from '~/features/guild/report/constants'
import { transformRoomLogsQueryParamsToSearchParams } from '~/features/guild/utils'
import type { RoomLogsDataTableRow, RoomLogsParams, RoomLogsSearchParams } from '~/features/room/report/types'
import { RoomApi } from '~/services/fm/room'
import CenterCircularProgress from '~/shared/components/center-circular-progress'
import useAppNavigate from '~/shared/navigation/use-app-navigate'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'
import type { MilliSeconds } from '~/shared/types/base/units'
import { EGuildRole } from '~/shared/types/services/user'

type ReactLocationState = {
  iconurl: string
  username: string
  roomId: number
}

const CreatorInfoWrapper = styled('div')`
  display: flex;
  align-items: center;
  .avatar {
    ${imageOptimizeRendering}
    margin-right: 20px;
    border-radius: 50%;
  }
`

const CreatorInfoUsername = styled('p')`
  margin: 0;
  padding: 0;
  color: #3d3d3d; /* TODO: 替换为 theme 里面的 palette，原始项目里的 $black-light */
  font-size: 25px;
`

const CreatorInfoCreatorId = styled('p')`
  margin: 0;
  padding: 0;
  margin-top: 4px;
  color: #3d3d3d; /* TODO: 替换为 theme 里面的 palette，原始项目里的 $black-light */
  font-size: 15px;
`

export const GuildReportDetail = () => {
  const { live_id } = useParams()
  const [searchParams, setSearchParams] = useAppSearchParam()
  const location = useLocation() as { state?: ReactLocationState }
  const navigate = useAppNavigate()

  let liveIdNumber = parseInt(live_id || '0')
  if (Number.isNaN(liveIdNumber)) {
    liveIdNumber = 0
  }

  const time = initDateTimeRange({
    excludeToday: true,
    daysRangeLimit: LIMIT_RANGE_OF_DAYS,
    startTime: searchParams.get('start_date')
      ? (moment(searchParams.get('start_date')).valueOf() as MilliSeconds)
      : undefined,
    endTime: searchParams.get('end_date')
      ? (moment(searchParams.get('end_date')).valueOf() as MilliSeconds)
      : undefined,
  })

  const [locationState] = useState(() => location.state)
  if (!locationState) {
    navigate('/guild/report')
  }

  const routeQuerySearchParams: RoomLogsSearchParams = {
    room_id: locationState?.roomId || 0,
    start_date: moment(time.startTime).format('YYYY-MM-DD'),
    end_date: moment(time.endTime).format('YYYY-MM-DD'),
    p: parseInt(searchParams.get('p') || '1') || 1,
  }

  const getChatroomLogsFetchQueryParams: RoomLogsParams = {
    room_id: routeQuerySearchParams.room_id,
    start_date: routeQuerySearchParams.start_date,
    end_date: routeQuerySearchParams.end_date,
    p: routeQuerySearchParams.p,
  }

  const { data: logsData } = RoomApi.getChatroomLogs.useQuery(getChatroomLogsFetchQueryParams)

  const logsDataPagination = logsData?.pagination && asMissEvanPagination(logsData.pagination)

  const logsDataTableRows =
    logsData?.data?.map((item, index) => {
      const row: RoomLogsDataTableRow = {
        ...item,
        id: index + 1,
      }

      return row
    }) || []

  const assignDefaultSearchParams = (modifier: (v: RoomLogsParams) => void) => {
    const params = { ...routeQuerySearchParams, p: 1 }
    modifier(params)
    return transformRoomLogsQueryParamsToSearchParams(params)
  }

  useMount(() => {
    if (
      logsDataPagination &&
      !Number.isNaN(logsDataPagination.page) &&
      routeQuerySearchParams.p !== logsDataPagination.page
    ) {
      setSearchParams(
        assignDefaultSearchParams((v) => {
          v.p = logsDataPagination.page
        })
      )
    }
  })

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const handleTimeRangeChange = (updatedTime: TTime) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.start_date = moment(updatedTime.startTime).format('YYYY-MM-DD')
        v.end_date = moment(updatedTime.endTime).format('YYYY-MM-DD')
      })
    )
  }

  const handlePageChange = (p: number) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.p = p
      })
    )
  }

  return (
    <GuildGuardWithRole requireOneOfRole={[EGuildRole.Owner, EGuildRole.Agent]}>
      {match(logsData)
        .with(undefined, () => <CenterCircularProgress />)
        .otherwise(() => (
          <>
            <Title>{['数据中心', GUILD_MIDDLE_TITLE]}</Title>
            <Stack spacing={4}>
              <StyledRouterLink
                to={{
                  pathname: `/guild/report`,
                  search: new URLSearchParams({
                    start_date: moment(time.startTime).format('YYYY-MM-DD'),
                    end_date: moment(time.endTime).format('YYYY-MM-DD'),
                  }).toString(),
                }}
                sx={{
                  fontSize: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <NavigateBeforeIcon />
                返回
              </StyledRouterLink>
              <CreatorInfoWrapper>
                <a href={`${api.MISSEVAN_LINK_PREFIX_URL}/${live_id}`} target="_blank">
                  <CircleLazyImage
                    src={api.removeUrlProtocol(locationState?.iconurl || '')}
                    alt={locationState?.username || ''}
                    width={80}
                    height={80}
                    className="avatar"
                  />
                </a>
                <div>
                  <CreatorInfoUsername className="username">{locationState?.username || ''}</CreatorInfoUsername>
                  <CreatorInfoCreatorId className="creator-id">ID: {liveIdNumber}</CreatorInfoCreatorId>
                </div>
              </CreatorInfoWrapper>
              <Form onSubmit={handleFormSubmit}>
                <Stack spacing={4}>
                  <DateRangeWrapper>
                    <DateRange time={time} limitDays={LIMIT_RANGE_OF_DAYS} onTimeRangeChange={handleTimeRangeChange} />
                    <Button variant="contained" onClick={handleFormSubmit}>
                      查询
                    </Button>
                  </DateRangeWrapper>
                  <GuildReportInfo startTime={time.startTime} endTime={time.endTime} liveId={liveIdNumber} />
                  <Stack spacing={2}>
                    <DataBlocksTitle>
                      <GuildTitle title="开播明细" />
                    </DataBlocksTitle>
                    <GuildReportInfoDataTable
                      rows={logsDataTableRows}
                      count={logsDataPagination?.page_count || 0}
                      page={routeQuerySearchParams.p}
                      onChange={(e, page) => handlePageChange(page)}
                    />
                  </Stack>
                </Stack>
              </Form>
            </Stack>
          </>
        ))}
    </GuildGuardWithRole>
  )
}
